import { Link } from "react-router";
import { useApi } from "../api.ts";
import { Button, Icon, LoadError, Loading, spot } from "../ui/index.tsx";
import { DetectionBadge, GameArt, GameStatusBadge } from "./_shared.tsx";
import type { Game } from "../../shared/types.ts";

export function GameCard({ game }: { game: Game }) {
  return (
    <article onMouseMove={spot} className="pane hud spotlight group flex flex-col overflow-hidden transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-accent/40">
      <div className="relative">
        <GameArt game={game} className="aspect-[460/215] transition-opacity [&_img]:opacity-80 group-hover:[&_img]:opacity-100" />
        <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent" />
        <span className="absolute left-3 top-3 rounded-md bg-bg/70 px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-muted backdrop-blur">{game.category}</span>
      </div>
      <div className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-lg font-semibold tracking-tight"><Link to={`/games/${game.id}`} className="after:absolute after:inset-0">{game.name}</Link></h3>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <GameStatusBadge status={game.status} />
            <DetectionBadge status={game.detection_status} />
          </div>
        </div>
        <p className="line-clamp-3 text-sm leading-relaxed text-muted">{game.description}</p>
        {game.features.length > 0 && (
          <ul className="flex flex-wrap gap-1.5 pt-1">
            {game.features.slice(0, 8).map((f) => <li key={f} className="rounded-md border border-line bg-raised px-2 py-0.5 text-xs text-muted">{f}</li>)}
            {game.features.length > 8 && <li className="px-1 py-0.5 text-xs text-faint">+{game.features.length - 8} more</li>}
          </ul>
        )}
        {game.pricing_tiers.length > 0 && (
          <div className="mt-auto flex items-center justify-between border-t border-line pt-4">
            <span className="text-sm text-muted">From <span className="font-semibold text-fg">{game.pricing_tiers[0]!.price}</span></span>
            <Link to={`/games/${game.id}`}><Button size="sm">View</Button></Link>
          </div>
        )}
      </div>
    </article>
  );
}

export function PublicHeading({ title, body, aside, kicker }: { title: string; body: string; aside?: React.ReactNode; kicker?: string }) {
  return (
    <div className="relative">
      <div className="relative mx-auto flex max-w-6xl flex-wrap items-end justify-between gap-4 px-6 pb-10 pt-14">
        <div className="max-w-2xl animate-rise">
          <div className="kicker mb-4 flex items-center gap-2 text-accent">
            <span className="size-1.5 rounded-full bg-accent shadow-[0_0_10px_var(--color-accent)]" />{kicker ?? `// ${title.toLowerCase()}`}
          </div>
          <h1 className="text-5xl font-bold uppercase leading-none tracking-tight sm:text-6xl">{title}</h1>
          <p className="mt-4 text-lg leading-relaxed text-muted">{body}</p>
        </div>
        {aside}
      </div>
      <div className="mx-auto h-px max-w-6xl bg-gradient-to-r from-accent/50 via-line to-transparent" />
    </div>
  );
}

export default function Games() {
  const games = useApi<Game[]>("/games");
  const list = games.data ?? [];
  return (
    <>
      <PublicHeading title="Games" body="Every title ExoWare supports, with live status and detection straight from our servers."
        aside={<Link to="/status" className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-fg"><Icon name="activity" className="size-4" />System status</Link>} />
      <section className="mx-auto max-w-6xl px-6 py-12">
        {games.loading && !games.data ? <Loading /> : games.error && !games.data ? <LoadError error={games.error} retry={games.reload} /> : list.length === 0 ? (
          <p className="rounded-[var(--radius-card)] border border-dashed border-line py-12 text-center text-sm text-muted">New titles are on the way.</p>
        ) : (
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">{list.map((g) => <GameCard key={g.id} game={g} />)}</div>
        )}
      </section>
    </>
  );
}
